const MEANINGFUL_SELECTOR = [
  '[data-testid]',
  'button',
  'a[href]',
  'input',
  'select',
  'textarea',
  'summary',
  '[role="button"]',
  '[role="link"]',
  '[role="menuitem"]',
  '[role="tab"]',
  '[role="row"]',
  '[role="gridcell"]',
  'th',
  'td',
  'tr',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'nav li',
].join(',');

export const ELEMENT_SELECTION_CHROME_SELECTOR = [
  '.feedback-overlay',
  '.feedback-launcher-wrap',
  '.feedback-launcher',
  '.feedback-menu',
  '.feedback-session-bar',
  '.feedback-element-selector',
].join(',');

const TRANSIENT_LAYER_SELECTOR = [
  '[role="tooltip"]',
  '.tooltip',
  '[data-tooltip-layer]',
  '.tippy-box',
  '.MuiTooltip-popper',
  '[data-radix-popper-content-wrapper]',
].join(',');

const NAMED_CONTAINER_PATTERN = /(^|[-_\s])(card|chart|panel|tile)([-_\s]|$)/i;
const FORM_FIELD_TAGS = new Set(['INPUT', 'SELECT', 'TEXTAREA']);
const DECORATIVE_TAGS = new Set(['SVG', 'PATH', 'SPAN', 'I']);

function normalizedText(value) {
  const text = String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
  return text || null;
}

function styleHides(element) {
  const style = window.getComputedStyle?.(element);
  return (
    style?.display === 'none' ||
    style?.visibility === 'hidden' ||
    style?.visibility === 'collapse' ||
    style?.opacity === '0'
  );
}

function hiddenByMarkup(element) {
  return Boolean(element.closest('[hidden], [aria-hidden="true"]'));
}

function visibleText(element) {
  if (!element || FORM_FIELD_TAGS.has(element.tagName)) return null;
  const walker = element.ownerDocument.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  const parts = [];
  let node = walker.nextNode();
  while (node) {
    const parent = node.parentElement;
    if (
      parent &&
      !hiddenByMarkup(parent) &&
      !styleHides(parent) &&
      !parent.closest('script, style, template')
    ) {
      const text = normalizedText(node.nodeValue);
      if (text) parts.push(text);
    }
    node = walker.nextNode();
  }
  return normalizedText(parts.join(' '));
}

function labelledByText(element) {
  const ids = String(element.getAttribute('aria-labelledby') || '')
    .split(/\s+/)
    .filter(Boolean);
  if (!ids.length) return null;
  return normalizedText(
    ids
      .map((id) => element.ownerDocument.getElementById(id))
      .filter(Boolean)
      .map((label) => visibleText(label))
      .filter(Boolean)
      .join(' '),
  );
}

export function getElementAccessibleName(element) {
  if (!(element instanceof Element)) return null;
  const ariaLabel = normalizedText(element.getAttribute('aria-label'));
  if (ariaLabel) return ariaLabel;
  const ariaLabelledBy = labelledByText(element);
  if (ariaLabelledBy) return ariaLabelledBy;
  const labels = Array.from(element.labels || [])
    .map((label) => visibleText(label))
    .filter(Boolean);
  if (labels.length) return normalizedText(labels.join(' '));
  const alt = normalizedText(element.getAttribute('alt'));
  if (alt) return alt;
  const title = normalizedText(element.getAttribute('title'));
  if (title) return title;
  return visibleText(element);
}

function implicitRole(element) {
  const tagName = element.tagName.toLowerCase();
  if (tagName === 'button') return 'button';
  if (tagName === 'a' && element.hasAttribute('href')) return 'link';
  if (tagName === 'select') return 'combobox';
  if (tagName === 'textarea') return 'textbox';
  if (tagName === 'summary') return 'button';
  if (tagName === 'tr') return 'row';
  if (tagName === 'th') return 'columnheader';
  if (tagName === 'td') return 'cell';
  if (/^h[1-6]$/.test(tagName)) return 'heading';
  if (tagName === 'input') {
    const type = String(element.getAttribute('type') || 'text').toLowerCase();
    if (['button', 'submit', 'reset', 'image'].includes(type)) return 'button';
    if (type === 'checkbox') return 'checkbox';
    if (type === 'radio') return 'radio';
    return 'textbox';
  }
  return null;
}

export function getElementViewportBounds(element) {
  if (!(element instanceof Element)) return null;
  const rect = element.getBoundingClientRect();
  const viewportWidth = Math.max(0, Number(window.innerWidth) || 0);
  const viewportHeight = Math.max(0, Number(window.innerHeight) || 0);
  const left = Math.max(0, Number(rect.left) || 0);
  const top = Math.max(0, Number(rect.top) || 0);
  const right = Math.min(viewportWidth, Number(rect.right) || left);
  const bottom = Math.min(viewportHeight, Number(rect.bottom) || top);
  const width = Math.max(0, right - left);
  const height = Math.max(0, bottom - top);
  if (width <= 0 || height <= 0) return null;
  return {
    x: Math.round(left),
    y: Math.round(top),
    width: Math.round(width),
    height: Math.round(height),
  };
}

function isVisibleCandidate(element) {
  return (
    !hiddenByMarkup(element) &&
    !styleHides(element) &&
    Boolean(getElementViewportBounds(element))
  );
}

function isRejectedRoot(element) {
  return (
    element === document.documentElement ||
    element === document.body ||
    element.matches('[data-testid="app-shell"], #root, #app')
  );
}

function isNamedContainer(element) {
  const tagName = element.tagName.toLowerCase();
  const className = typeof element.className === 'string' ? element.className : '';
  const containerSignal =
    ['article', 'section'].includes(tagName) ||
    element.hasAttribute('data-chart') ||
    element.hasAttribute('data-card') ||
    NAMED_CONTAINER_PATTERN.test(className);
  if (!containerSignal || !getElementAccessibleName(element)) return false;
  const display = window.getComputedStyle?.(element)?.display;
  return !['inline', 'contents', 'none'].includes(display);
}

function decorativeWithoutOwnName(element) {
  if (!DECORATIVE_TAGS.has(element.tagName)) return false;
  return !(
    normalizedText(element.getAttribute('aria-label')) ||
    normalizedText(element.getAttribute('aria-labelledby')) ||
    normalizedText(element.getAttribute('title'))
  );
}

export function isElementSelectionChrome(target) {
  return Boolean(
    target instanceof Element && target.closest(ELEMENT_SELECTION_CHROME_SELECTOR),
  );
}

export function resolveMeaningfulElement(target) {
  if (!(target instanceof Element)) return null;
  if (isElementSelectionChrome(target) || target.closest(TRANSIENT_LAYER_SELECTOR)) {
    return null;
  }
  let current = target;
  while (current instanceof Element) {
    if (isRejectedRoot(current)) return null;
    if (
      isVisibleCandidate(current) &&
      !decorativeWithoutOwnName(current) &&
      (current.matches(MEANINGFUL_SELECTOR) || isNamedContainer(current))
    ) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

export function getElementSelectionCandidates(root = document) {
  const candidates = [];
  const seen = new Set();
  for (const target of root.querySelectorAll(`${MEANINGFUL_SELECTOR}, article, section`)) {
    const candidate = resolveMeaningfulElement(target);
    if (!candidate || seen.has(candidate)) continue;
    seen.add(candidate);
    candidates.push(candidate);
  }
  return candidates;
}

function stableId(id) {
  const value = String(id || '');
  if (!/^[A-Za-z][A-Za-z0-9_-]{1,79}$/.test(value)) return false;
  if (/^[a-f0-9]{8,}$/i.test(value)) return false;
  if (/^[a-f0-9]{8}-[a-f0-9-]{20,}$/i.test(value)) return false;
  const digits = (value.match(/\d/g) || []).length;
  return digits <= Math.max(4, Math.floor(value.length / 2));
}

function escapeAttributeValue(value) {
  return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

export function buildElementSelectionMetadata(element, bounds = null) {
  if (!(element instanceof Element)) {
    return {
      tagName: null,
      role: null,
      accessibleName: null,
      testId: null,
      id: null,
      textSnippet: null,
      bounds: bounds || null,
      selectorHint: null,
    };
  }
  const tagName = element.tagName.toLowerCase();
  const testId = normalizedText(element.getAttribute('data-testid'));
  const id = normalizedText(element.id);
  const snippet = FORM_FIELD_TAGS.has(element.tagName) ? null : visibleText(element);
  return {
    tagName,
    role: normalizedText(element.getAttribute('role')) || implicitRole(element),
    accessibleName: getElementAccessibleName(element),
    testId,
    id,
    textSnippet: snippet ? snippet.slice(0, 120) : null,
    bounds: bounds || getElementViewportBounds(element),
    selectorHint: testId
      ? `[data-testid="${escapeAttributeValue(testId)}"]`
      : stableId(id)
        ? `#${id}`
        : null,
  };
}
